import styles from './ProjectForecastReports.module.css'
import { AgGridReact } from 'ag-grid-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { defaultColDef } from '../../MainGrids/gridConfig/DefaultColDef'
import { AG_GRID_LOCALE_SV } from '../../MainGrids/gridConfig/SwedishLocale'
import LoadingSpinner from '../../StatusIndicators/LoadingSpinner'
import { UnableToLoadData } from '../../SystemState/ErrorManagement/UnableToLoadData/UnableToLoadData'
import { DefaultSelect } from '../../Inputs/DefaultSelect/DefaultSelect'
import GridFilter from '../../GridFilter/GridFilter'
import { Button, Tooltip } from '@fluentui/react-components'
import { ArrowCounterclockwiseRegular } from '@fluentui/react-icons'
import { getProjectForecastReportColumnDefs } from './ProjectForecastReportsColDefs'
import {
  buildProjectForecastUrl,
  getLatestClosedPeriod,
  getYearOptions,
  keyByPeriod,
  periodByKey,
  periodOptions,
} from './utils/projectForecastReportsUtils'
import { useProjectForecastReports } from '../../../fetchData/queries/reports/useProjectForecastReports'
import CompleteProjectForecastReportsButton from './CompleteProjectForecastReportsButton'
import { ProjectForecastReportRow } from '../../../interfaces/systemSettings/ProjectForecastReport'
import { useBulkCompleteProjectForecast } from './useBulkCompleteProjectForecast'
import { BulkCompleteProgressModal } from './BulkCompleteProgressModal/BulkCompleteProgressModal'
import { ActionConfirmationModal } from '../../Projektprognos/Snapshots/Shared/ActionConfirmationModal'

const ProjectForecastReports = () => {
  const { year: defaultYear, period: defaultPeriod } = getLatestClosedPeriod(
    new Date()
  )
  const [selectedYear, setSelectedYear] = useState<number>(defaultYear)
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<number>(
    keyByPeriod[defaultPeriod]
  )
  const [selectedRows, setSelectedRows] = useState<ProjectForecastReportRow[]>(
    []
  )
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const {
    state: bulkCompleteState,
    startBulkComplete,
    cancel: cancelBulkComplete,
    reset: resetBulkComplete,
  } = useBulkCompleteProjectForecast()
  const gridRef = useRef<AgGridReact>(null)
  const gridRefs = useMemo(() => [gridRef], [])
  const queryClient = useQueryClient()

  const getProjectForecastUrl = useCallback(
    (projectId: string) =>
      projectId
        ? buildProjectForecastUrl(window.location.origin, projectId)
        : '',
    []
  )

  const selectedPeriod = periodByKey[selectedPeriodKey] ?? 'P1'
  const projectForecastReports = useProjectForecastReports(
    selectedYear,
    selectedPeriod
  )

  const handleInvalidateCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['projectForecastReports', selectedYear, selectedPeriod],
    })
  }, [queryClient, selectedYear, selectedPeriod])

  const yearOptions = useMemo(
    () => getYearOptions(selectedYear),
    [selectedYear]
  )

  const columnDefs = useMemo(
    () => getProjectForecastReportColumnDefs(getProjectForecastUrl),
    [getProjectForecastUrl]
  )

  const reportDefaultColDef = useMemo(
    () => ({
      ...defaultColDef,
      filter: true,
      floatingFilter: true,
    }),
    []
  )

  const handleSelectionChanged = useCallback(() => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes() ?? []
    const selectedRowData = selectedNodes
      .map((node) => node.data)
      .filter((data): data is ProjectForecastReportRow => data !== undefined)
    setSelectedRows(selectedRowData)
  }, [])

  const handleSelectAllOngoing = useCallback(() => {
    gridRef.current?.api.forEachNode((node) => {
      if (node.data?.status === 'Ongoing') {
        node.setSelected(true)
      }
    })
  }, [])

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api.deselectAll()
  }, [])

  const handleOpenConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(true)
  }, [])

  const handleCloseConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false)
  }, [])

  const handleConfirmBulkComplete = useCallback(() => {
    const ongoingReports = selectedRows.filter(
      (row) => row.status === 'Ongoing' || row.status === 'Open'
    )

    if (ongoingReports.length === 0) {
      return
    }

    setIsConfirmModalOpen(false)
    resetBulkComplete()

    // Use setTimeout to ensure modal closes and state settles before starting
    setTimeout(() => {
      setIsBulkModalOpen(true)
      // Add additional delay before starting API calls to ensure everything is ready
      setTimeout(() => {
        startBulkComplete(ongoingReports, selectedYear, selectedPeriod)
      }, 300)
    }, 100)
  }, [
    selectedRows,
    selectedYear,
    selectedPeriod,
    resetBulkComplete,
    startBulkComplete,
  ])

  const handleCloseBulkModal = useCallback(() => {
    setIsBulkModalOpen(false)
    resetBulkComplete()
    gridRef.current?.api.deselectAll()
  }, [resetBulkComplete])

  const ongoingSelectedCount = selectedRows.filter(
    (row) => row.status === 'Ongoing' || row.status === 'Open'
  ).length

  return (
    <div className={styles.container} data-testid="ProjectForecastReports">
      <div className={styles.filters}>
        <GridFilter
          gridRefs={gridRefs}
          elementId="projectForecastReportsQuickFilter"
        />

        <div className={styles.selectFilters}>
          <Tooltip content="Invalidera cache" relationship="label">
            <Button
              icon={<ArrowCounterclockwiseRegular />}
              appearance="subtle"
              onClick={handleInvalidateCache}
              disabled={projectForecastReports.isLoading}
            />
          </Tooltip>
          <div className={styles.filterField}>
            <DefaultSelect
              label="År"
              options={yearOptions}
              defaultSelectedKey={selectedYear}
              setSelectedOption={setSelectedYear}
              selectWidth={130}
            />
          </div>

          <div className={styles.filterField}>
            <DefaultSelect
              label="Period"
              options={periodOptions}
              defaultSelectedKey={selectedPeriodKey}
              setSelectedOption={setSelectedPeriodKey}
              selectWidth={120}
            />
          </div>

          <CompleteProjectForecastReportsButton
            year={selectedYear}
            period={selectedPeriod}
          />

          <Button
            appearance="subtle"
            onClick={handleSelectAllOngoing}
            disabled={projectForecastReports.isLoading}
          >
            Välj alla pågående
          </Button>

          <Button
            appearance="subtle"
            onClick={handleClearSelection}
            disabled={selectedRows.length === 0}
          >
            Rensa markering
          </Button>

          <Tooltip
            content={
              ongoingSelectedCount === 0
                ? 'Välj minst en pågående rapport'
                : `Färdigställ ${ongoingSelectedCount} markerade rapport${
                    ongoingSelectedCount > 1 ? 'er' : ''
                  }`
            }
            relationship="label"
          >
            <Button
              appearance="primary"
              onClick={handleOpenConfirmModal}
              disabled={ongoingSelectedCount === 0}
            >
              Färdigställ markerade ({ongoingSelectedCount})
            </Button>
          </Tooltip>
        </div>
      </div>

      <ActionConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmBulkComplete}
        isLoading={false}
        error={null}
        onReset={() => {}}
        title="Färdigställ markerade rapporter"
        message={`Du är på väg att färdigställa ${ongoingSelectedCount} markerade prognosrapporter för ${selectedPeriod} ${selectedYear}. Rapporterna låses för redigering och detta kan inte ångras. Är du säker?`}
        confirmLabel="Färdigställ"
      />

      <BulkCompleteProgressModal
        isOpen={isBulkModalOpen}
        onClose={handleCloseBulkModal}
        isProcessing={bulkCompleteState.isProcessing}
        currentIndex={bulkCompleteState.currentIndex}
        totalCount={bulkCompleteState.totalCount}
        currentProjectName={bulkCompleteState.currentProjectName}
        successCount={bulkCompleteState.successCount}
        failures={bulkCompleteState.failures}
        isComplete={bulkCompleteState.isComplete}
        onCancel={cancelBulkComplete}
      />

      {projectForecastReports.error ? (
        <div className={styles.messageContainer}>
          <UnableToLoadData message="Prognosuppföljningrapporter kunde ej laddas" />
        </div>
      ) : (
        <div className={`ag-theme-balham ${styles.gridContainer}`}>
          {projectForecastReports.isLoading ? (
            <LoadingSpinner text="Laddar prognosuppföljningrapporter..." />
          ) : (
            <div className={styles.gridInner}>
              <AgGridReact
                ref={gridRef}
                rowData={projectForecastReports.data ?? []}
                columnDefs={columnDefs}
                defaultColDef={reportDefaultColDef}
                localeText={AG_GRID_LOCALE_SV}
                rowSelection={'multiple'}
                cacheQuickFilter={true}
                suppressCopyRowsToClipboard={true}
                enableRangeSelection={false}
                suppressContextMenu={true}
                paginationAutoPageSize={true}
                pagination={true}
                onSelectionChanged={handleSelectionChanged}
              />
            </div>
          )}

          {!projectForecastReports.isLoading &&
            !projectForecastReports.error &&
            (projectForecastReports.data?.length ?? 0) === 0 && (
              <div className={styles.messageContainer}>
                <UnableToLoadData message="Inga prognosuppföljningrapporter hittades" />
              </div>
            )}
        </div>
      )}
    </div>
  )
}

export default ProjectForecastReports
